import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { ReactNode, useState } from 'react'
import useBriefContext from '../../../../Hooks/Brief/useBriefContext'
import './EmailInput.less'
import LoadingSpinner from '../../../../Components/LoadingSpinner'
import FormPage from '../../../../Components/Form/Page'
import CountryFields from './Fields/CountryFields'
import EmailField from './Fields/EmailField'
import useSubmitOnClick from '../../../../Hooks/Form/useSubmitOnClick'
import getAdjacentSteps from '../../../../Logic/Form/Authorization/getAdjacentSteps'

interface EmailInputProps {
  path: string
  metaTitle: string
  heading: string
  className?: string
  header?: ReactNode
  emailCaption?: ReactNode
  emailHeading?: ReactNode
}

const EmailInput = ({
  path,
  heading,
  header,
  metaTitle,
  emailCaption,
  emailHeading,
  className = '',
}: EmailInputProps): JSX.Element => {
  const { brief, dispatchBrief } = useBriefContext()
  const { triageUser } = useGlobalAuth()
  const { submitOnClick, formRef } = useSubmitOnClick()

  const [submissionLoading, setSubmissionLoading] = useState(false)
  const [email, setEmail] = useState(brief.guest?.email || '')
  const [showCountryStep, setShowCountryStep] = useState(false)
  const [blockDefaultNavigation, setBlockDefaultNavigation] = useState(false)
  const [country, setCountry] = useState(brief.guest?.country ?? undefined)
  const { isStepComplete } = getAdjacentSteps(path, brief)

  // Email Step FormPage options

  const canEmailStepContinue = !!email

  const onEmailStepContinue = async () => {
    setSubmissionLoading(true)
    const {
      userId,
      isBusinessUser,
      isDomainValid: unambiguousOrigin,
      hasExistingAccount,
    } = await triageUser(email)
    const requireCountryTriage = !unambiguousOrigin && !hasExistingAccount && isBusinessUser

    if (requireCountryTriage) {
      setShowCountryStep(true)
      setBlockDefaultNavigation(true)
    }

    const guestAttributes = {
      email,
      unambiguousOrigin,
      ...(!requireCountryTriage ? { isBusinessUser } : {}),
    }
    dispatchBrief({
      updateAttributes: { userId, guest: guestAttributes },
    })
    setSubmissionLoading(false)
  }

  // Country Step FormPage options

  const canCountryStepContinue = isStepComplete({ guest: { email: brief.guest?.email, country } })

  const onCountryStepContinue = async () => {
    setBlockDefaultNavigation(false)

    if (country == undefined) throw new Error('Country undefined in triage country form')
    if (email === undefined) throw new Error('Email undefined in triage country form')

    setSubmissionLoading(true)

    const { userId, isBusinessUser } = await triageUser(email, country)

    dispatchBrief({
      updateAttributes: { userId, guest: { isBusinessUser, country } },
    })
  }

  const canContinue = showCountryStep ? canCountryStepContinue : canEmailStepContinue
  const onContinue = showCountryStep ? onCountryStepContinue : onEmailStepContinue
  const onBack = showCountryStep ? () => setShowCountryStep(false) : undefined
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const afterContinue = blockDefaultNavigation ? () => {} : undefined

  return (
    <FormPage
      className={`triage-page ${submissionLoading ? 'loading' : ''} ${
        showCountryStep ? 'country-step' : 'email-step'
      } ${className}`}
      path={path}
      heading={heading}
      header={header}
      metaTitle={metaTitle}
      onContinue={onContinue}
      afterContinue={afterContinue}
      canContinue={canContinue}
      onBack={onBack}
      formRef={formRef}
      disableNav={submissionLoading}>
      <fieldset aria-busy={submissionLoading} id='triage-fieldset' className='input-section'>
        {submissionLoading ? (
          <LoadingSpinner progressId='triage-fieldset' fadeIn={true} />
        ) : showCountryStep ? (
          <CountryFields country={country} setCountry={setCountry} onCountryClick={submitOnClick} />
        ) : (
          <EmailField
            email={email}
            setEmail={setEmail}
            caption={emailCaption}
            heading={emailHeading}
          />
        )}
      </fieldset>
    </FormPage>
  )
}

export default EmailInput
